/**
 * Extract HTTP status from thrown errors (ApiError, serialized server actions).
 * Does not scrape arbitrary digits from message text — that false-positives when
 * upstream errors mention GitHub "403" / similar inside a non-auth failure.
 */
export function extractHttpStatus(
  error: Error & { digest?: string },
): number | undefined {
  const record = error as unknown as Record<string, unknown>;
  const direct = record.status;
  if (typeof direct === "number" && Number.isFinite(direct)) {
    return direct;
  }

  if (error.message) {
    try {
      const parsed = JSON.parse(error.message) as { status?: unknown };
      if (typeof parsed.status === "number" && Number.isFinite(parsed.status)) {
        return parsed.status;
      }
    } catch {
      // not JSON
    }

    // Prefer structured status markers only (not bare "403" in prose).
    const structured = /\bstatus(?:Code)?["']?\s*[:=]\s*["']?(4\d{2}|50[023])\b/i.exec(
      error.message,
    );
    if (structured) {
      return Number.parseInt(structured[1], 10);
    }
  }

  return undefined;
}

const SERVER_ACTION_SKEW_RE =
  /Failed to find Server Action|failed-to-find-server-action|older or newer deployment/i;

const SKEW_RELOAD_KEY = "kriya:server-action-skew-reload";

/** True when Next.js rejected a Server Action ID from a different deploy (version skew). */
export function isServerActionSkewError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === "string") return SERVER_ACTION_SKEW_RE.test(error);
  if (error instanceof Error) {
    return (
      SERVER_ACTION_SKEW_RE.test(error.message) ||
      SERVER_ACTION_SKEW_RE.test(error.name)
    );
  }
  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" && SERVER_ACTION_SKEW_RE.test(message);
  }
  return false;
}

/**
 * Open tabs keep old Server Action IDs after a Netlify deploy. Skew protection
 * (NETLIFY_NEXT_SKEW_PROTECTION) reduces this for *new* page loads; existing
 * sessions still need a full reload. Toast once and reload (guarded per tab).
 */
export function recoverFromServerActionSkew(error: unknown): boolean {
  if (!isServerActionSkewError(error) || typeof window === "undefined") {
    return false;
  }

  try {
    if (sessionStorage.getItem(SKEW_RELOAD_KEY) === "1") {
      return true;
    }
    sessionStorage.setItem(SKEW_RELOAD_KEY, "1");
  } catch {
    // sessionStorage blocked — still attempt reload
  }

  void import("sonner").then(({ toast }) => {
    toast.dismiss();
    toast.message("App was updated. Refreshing…");
  });

  window.setTimeout(() => {
    window.location.reload();
  }, 600);

  return true;
}

/** Call after a successful page load so a later deploy can recover again. */
export function clearServerActionSkewReloadGuard(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SKEW_RELOAD_KEY);
  } catch {
    // ignore
  }
}
